import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatCardModule } from "@angular/material/card";
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { finalize, Subject, take } from 'rxjs';
import { AuthenticationService } from '../../services/authentication.service';
import { NotificationService } from '../../services/notification.service';
import { UsersService } from '../../services/users.service';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SearchBarComponent } from "../../shared/searchbar/searchbar.component";
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";


@Component({
  selector: 'app-users',
  imports: [MatCardModule, SearchBarComponent, SharedTableComponent, LoadingComponent],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, OnDestroy {

  displayedColumns: string[] = ['firstName', 'lastName', 'email', 'actions'];
  columnLabels: Record<string, string> = {
    email: 'Email',
    firstName: 'Nome',
    lastName: 'Cognome',
    actions: ''
  };

  isLoading = false;
  hasError = false;
  isLoadingFullScreen = false;

  usersListLoaded$ = new Subject<void>();

  // Stato query
  sort = 'id';
  direction = 'DESC';
  filter = {
    email: '' 
  }

  // Paginazione server-side
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;
  limit = 10; //pageSize iniziale
  offset = 0;

  // DataSource tabella
  usersDataSource = new MatTableDataSource<any>();

  // Opzioni select "role"
  roleSelectOptions = [{ value: '', label: '' }];

  // campi nascosti per la modale
  hiddenFieldsValues: Record<string, any> = {
    typology: 'BACKOFFICE'
  };

  constructor(
    private readonly dialog: MatDialog,
    private readonly usersService: UsersService,
    private readonly notificationService: NotificationService,
    private readonly authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.loadUsersData(this.offset, this.limit, this.sort, this.direction , {});
    // Recupero ruoli per la select
    this.usersService.getRolesList().subscribe({
      next: (response) => {
        const roleOptions = response as Array<{ value: string; label: string }>;
        this.roleSelectOptions = roleOptions.map((x: any) => ({
          value: x.toUpperCase(),
          label: x
        }));
      },
      error: () => {
        this.roleSelectOptions = [];
      }
    });
  }

  private currentFilter() {
    const q = this.filter.email?.trim() ?? '';
    return q ? { email: q } : {};
  }

  loadUsersData(
    offset: number,
    limit: number,
    sort: string,
    direction: string,
    filter: any
  ): void {
    this.isLoading = true;
    this.hasError = false;
    this.usersService.getUsersList(offset, limit, sort, direction, filter).pipe(finalize(() => { 
      this.isLoading = false;
      this.usersListLoaded$.next();
     })).subscribe({
      next: (response) => {
        const content = response?.content.filter((x: any) => x.email !== null) ?? [];
        this.usersDataSource.data = content;
        console.log('dati utenti', content);
        this.totalItems = response?.totalElements ?? 0;
        // allinea lo stato locale con quanto ritorna il BE
        this.pageIndex = response?.pageable.offset/response?.pageable.limit;
        this.limit  = response?.pageable.limit ?? limit; // limit è pageSize
        this.offset = response?.pageable.offset ?? (this.pageIndex * this.limit);
        console.log('paginazione', this.limit);
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.usersDataSource.data = [];
        this.totalItems = 0;
        this.hasError = true;
      },
    });
  }

  // Filtro
  onFilterChanged(value: any) {
    this.filter.email = value?.email ?? '';
    this.pageIndex = 0;
    this.loadUsersData(this.offset, this.limit, this.sort, this.direction, this.currentFilter());
  }

  // Ordinamento
  onSortChanged(sort: Sort) {
    this.sort = sort.active;
    this.direction = sort.direction.toUpperCase() || 'ASC';
    console.log('sorting', this.sort, '', this.direction);
    this.pageIndex = 0;
    this.loadUsersData(this.offset, this.limit, this.sort, this.direction, this.currentFilter());
  }
  
  // Cambio pagina / page size (server-side)
  
  onPageChanged(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.limit = event.pageSize;
    this.offset = this.pageIndex * this.limit;
    this.loadUsersData(this.offset, this.limit, this.sort, this.direction, this.currentFilter());
  }

  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {

    console.log('event', event);
    const dialogRef = this.dialog.open(ModalComponent, {
      width: '400px',
      data: {
        modalType: 'users',
        action: event.type,
        payload: event.data,
        visibleFields: ['firstName', 'lastName', 'email', 'password', 'role', 'enabled'],
        fieldTypes: {
          firstName: 'input',
          lastName: 'input',
          email: 'input',
          password: 'input',
          role: 'select',
          enabled: 'checkbox'
        },
        readonlyFields: {
          edit: ['firstName','lastName', 'email'],
          add: ['enabled']
        },
        nonRequiredFields: { edit: ['password'] },
        labels: {
          firstName: 'Nome',
          lastName: 'Cognome',
          email: 'Email',
          password: 'Password',
          role: 'Ruolo',
          enabled: 'Utenza attiva'
        },
        selectOptions: {
          role: this.roleSelectOptions
        },
        hiddenFields: ['typology'],
        hiddenFieldsValues: this.hiddenFieldsValues
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.isLoadingFullScreen = true;

      if (event.type === 'add') {
        console.log('result add', result);
        this.usersService.addNewUser(result).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
             this.loadUsersData(this.offset, this.limit, this.sort, this.direction , this.currentFilter());
            this.usersListLoaded$.pipe(take(1)).subscribe(()=> {
              this.notificationService.entitySuccess('add', 'Utente');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('add', 'Utente');
          }
        });

      } else if (event.type === 'edit') {
        console.log('result edit', result);
        const index = this.usersDataSource.data.findIndex((e: any) => e.id === result.id);
        console.log('index', index);
        if (index !== -1) {
          this.usersService.editUser(result).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
            next: () => {
              this.loadUsersData(this.offset, this.limit, this.sort, this.direction , this.currentFilter());
              this.usersListLoaded$.pipe(take(1)).subscribe(()=> {
                this.notificationService.entitySuccess('update', 'Utente');
              })
            },
            error: (err) => {
              console.log('errore nella richiesta', err);
              this.notificationService.entityError('update', 'Utente');
            }
          });
        }

      } else if (event.type === 'delete') {
        console.log('result delete', result);
        this.usersService.deleteUser(result.id).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            if (this.pageIndex > 0 && this.usersDataSource.data.length === 1) {
              this.pageIndex = this.pageIndex - 1;
            }
            this.loadUsersData(this.offset, this.limit, this.sort, this.direction , this.currentFilter());
            this.usersListLoaded$.pipe(take(1)).subscribe(() => {
              this.notificationService.entitySuccess('delete', 'Utente');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('delete', 'Utente');
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.usersListLoaded$.complete();
  }
}
